// LivestreamFeedDataFetcher.jsx
import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { LIVESTREAM_DETAIL_ON_FEED_FETCH_INTERVAL_SECONDS } from '../RemoteConfigKeys.js';

import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import getNetworkingData from '../selector/getNetworkingData.js';
import { getRealtimeChannels } from '../selector/getRealtimeComputedData.js';

import usePollingLivestreamDetailData from '../hook/usePollingLivestreamDetailData.js';
import usePollingLivestreamFundingDetail from '../hook/usePollingLivestreamFundingDetail.js';

import {
  RealtimeFeatureName,
  RealtimeServiceConnectionStatus,
} from '../resource/realtimeConstants.js';

export const LivestreamFeedDataFetcher = ({
  streamId = '',
  realtimeFeature = RealtimeFeatureName.LIVESTREAM_PREVIEW,
}) => {
  const intervalSeconds = useSelector(state =>
    getRemoteConfigData(state, LIVESTREAM_DETAIL_ON_FEED_FETCH_INTERVAL_SECONDS)
  );

  const runningShowId = useSelector(state =>
    getLiveStreamData(state, [streamId], 'runningShowId')
  );

  const connectedTimestamp = useSelector(state => {
    const channels = getRealtimeChannels(state, realtimeFeature, streamId);

    const isAllConnected = channels.every(
      item => RealtimeServiceConnectionStatus.CONNECTED === item.status?.status
    );

    if (isAllConnected) {
      return Math.min(...channels.map(item => item.status?.timestamp || 0));
    }

    return 0;
  });

  const getNeedFetchData = useCallback(
    fetchedTimestamp => fetchedTimestamp <= connectedTimestamp,
    [connectedTimestamp]
  );

  const livestreamDetailFetchedTimetamp =
    useSelector(state =>
      getNetworkingData(
        state,
        ['fetch', 'livestream', streamId],
        'fetchedTimestamp'
      )
    ) || 0;

  // fetch livestream detail interval
  usePollingLivestreamDetailData({
    streamId,
    intervalSeconds: intervalSeconds,
    canFetchData:
      streamId != null && getNeedFetchData(livestreamDetailFetchedTimetamp),
  });

  const fundingDetailFetchedTimestamp =
    useSelector(state =>
      getNetworkingData(
        state,
        ['fetch', 'livestream', streamId, 'fundingDetail', runningShowId],
        'fetchedTimestamp'
      )
    ) || 0;

  // fetch livestream funding detail interval
  usePollingLivestreamFundingDetail({
    streamId,
    runningShowId,
    intervalSeconds: intervalSeconds,
    canFetchData:
      streamId != null &&
      runningShowId != null &&
      getNeedFetchData(fundingDetailFetchedTimestamp),
  });

  return null;
};

LivestreamFeedDataFetcher.propTypes = {
  streamId: PropTypes.string,
  realtimeFeature: PropTypes.string,
};

export default LivestreamFeedDataFetcher;
